/**
 * Aba "E-mail" da página /models — conteúdo movido da antiga página
 * /templates (Modelos de e-mail + Log de envio). A rota /templates agora
 * redireciona para /models.
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  TEMPLATE_VARIABLE_NAMES,
  type EmailTemplate,
  type SentEmail,
  type TemplateBody,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDateTime } from '@/lib/utils';
import { errorMessage } from '@/features/desks/desks-queries';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { emailTemplatesOptions, mailLogOptions } from '@/features/templates/templates-queries';

/** Chips exibidos no editor — a lista canônica de variáveis vem do shared. */
const TEMPLATE_VARIABLES = TEMPLATE_VARIABLE_NAMES.map((name) => `{{${name}}}`);

export function EmailModelsTab() {
  const [tab, setTab] = useState('modelos');

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <div className="px-4 pt-3">
        <TabsList>
          <TabsTrigger value="modelos" className="px-3">
            Modelos de e-mail
          </TabsTrigger>
          <TabsTrigger value="log" className="px-3">
            Log de envio
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="modelos">
        <EmailTemplatesTab />
      </TabsContent>
      <TabsContent value="log">
        <MailLogTab />
      </TabsContent>
    </Tabs>
  );
}

// ── Modelos de e-mail ───────────────────────────────────────────────────────

function EmailTemplatesTab() {
  const queryClient = useQueryClient();
  const templates = useQuery(emailTemplatesOptions());
  const [dialog, setDialog] = useState<{ template: EmailTemplate | null } | null>(null);

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/mail/templates/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['erp', 'mail', 'templates'] }),
  });

  const list = templates.data ?? [];

  return (
    <div>
      <div className="flex justify-end px-4 pt-3">
        <Button size="sm" onClick={() => setDialog({ template: null })}>
          <Plus className="size-4" strokeWidth={2.5} />
          Novo modelo
        </Button>
      </div>
      {templates.isPending ? (
        <div className="space-y-2 p-4" aria-busy>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : templates.isError ? (
        <EmptyState message={`Erro ao carregar modelos: ${errorMessage(templates.error)}`} />
      ) : list.length === 0 ? (
        <EmptyState className="h-52" message="Nenhum modelo cadastrado" />
      ) : (
        <div className="overflow-x-auto p-4 pt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-10 px-4 font-semibold">Nome</th>
                <th className="h-10 px-4 font-semibold">Assunto</th>
                <th className="h-10 px-4 font-semibold">Status</th>
                <th className="h-10 px-4 font-semibold">Atualizado em</th>
                <th className="h-10 px-4 text-right font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id} className="h-10 border-b border-divider last:border-0">
                  <td className="max-w-64 truncate px-4 font-medium">{t.name}</td>
                  <td className="max-w-80 truncate px-4 text-muted-foreground">{t.subject}</td>
                  <td className="px-4 whitespace-nowrap">
                    {t.active ? (
                      <Badge variant="success">Ativo</Badge>
                    ) : (
                      <Badge>Inativo</Badge>
                    )}
                  </td>
                  <td className="px-4 whitespace-nowrap text-muted-foreground">
                    {formatDateTime(t.updatedAt)}
                  </td>
                  <td className="px-4">
                    <div className="flex items-center justify-end gap-0.5">
                      <DeskRowAction label="Editar modelo" onClick={() => setDialog({ template: t })}>
                        <Pencil className="size-3.5" />
                      </DeskRowAction>
                      <DeskRowAction
                        label="Excluir modelo"
                        destructive
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(`Excluir o modelo "${t.name}"?`)) remove.mutate(t.id);
                        }}
                      >
                        <Trash2 className="size-3.5" />
                      </DeskRowAction>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {remove.error ? (
        <p className="px-4 pb-3 text-sm text-destructive">{errorMessage(remove.error)}</p>
      ) : null}
      {dialog ? (
        <EmailTemplateDialog template={dialog.template} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

function EmailTemplateDialog({
  template,
  onClose,
}: {
  template: EmailTemplate | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [active, setActive] = useState(template?.active ?? true);

  const save = useMutation({
    mutationFn: async (payload: TemplateBody) => {
      const res = template
        ? await api<EmailTemplate>(`/mail/templates/${template.id}`, {
            method: 'PUT',
            body: payload,
          })
        : await api<EmailTemplate>('/mail/templates', { method: 'POST', body: payload });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'mail', 'templates'] });
      onClose();
    },
  });

  const valid = name.trim() && subject.trim() && body.trim();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    save.mutate({ name: name.trim(), subject: subject.trim(), body, active });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={template ? 'Editar modelo' : 'Novo modelo'}
      className="sm:max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="template-name">
              Nome <span className="text-destructive">*</span>
            </Label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="template-subject">
              Assunto <span className="text-destructive">*</span>
            </Label>
            <Input
              id="template-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              placeholder="Ex.: Ticket #{{ticket.number}} atualizado"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="template-body">
            Corpo (HTML) <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="template-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            rows={10}
            className="min-h-48 font-mono text-[13px]"
            placeholder={'<p>Olá {{client.name}},</p>\n<p>O ticket #{{ticket.number}} — {{ticket.title}} foi atualizado.</p>'}
          />
        </div>
        <div className="rounded-md border border-divider bg-muted/60 p-3">
          <p className="text-xs font-medium text-muted-foreground">Variáveis disponíveis</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {TEMPLATE_VARIABLES.map((v) => (
              <code
                key={v}
                className="rounded-sm border border-border bg-card px-1.5 py-0.5 font-mono text-xs"
              >
                {v}
              </code>
            ))}
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <Switch checked={active} onCheckedChange={setActive} />
          Modelo ativo
        </label>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !valid}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ── Log de envio ────────────────────────────────────────────────────────────

function statusBadge(email: SentEmail) {
  switch (email.status) {
    case 'sent':
      return <Badge variant="success">Enviado</Badge>;
    case 'failed':
      return <Badge variant="destructive">Falhou</Badge>;
    case 'skipped':
      return <Badge>Não enviado</Badge>;
    // bloqueado pela lista de supressão (bounce permanente / reclamação de
    // spam) — visualmente distinto de "não enviado" porque a causa é o
    // destinatário, não a configuração
    case 'suppressed':
      return <Badge variant="destructive">Bloqueado</Badge>;
  }
}

function MailLogTab() {
  const log = useQuery(mailLogOptions());
  const entries = log.data ?? [];

  return (
    <div>
      {log.isPending ? (
        <div className="space-y-2 p-4" aria-busy>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : log.isError ? (
        <EmptyState message={`Erro ao carregar log de envio: ${errorMessage(log.error)}`} />
      ) : entries.length === 0 ? (
        <EmptyState className="h-52" message="Nenhum e-mail registrado" />
      ) : (
        <div className="overflow-x-auto p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-10 px-4 font-semibold">Data</th>
                <th className="h-10 px-4 font-semibold">Para</th>
                <th className="h-10 px-4 font-semibold">Assunto</th>
                <th className="h-10 px-4 font-semibold">Ticket</th>
                <th className="h-10 px-4 font-semibold">Status</th>
                <th className="h-10 px-4 font-semibold">Erro</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="h-10 border-b border-divider last:border-0">
                  <td className="px-4 whitespace-nowrap text-muted-foreground">
                    {formatDateTime(e.createdAt)}
                  </td>
                  <td className="max-w-56 truncate px-4">{e.toEmail}</td>
                  <td className="max-w-72 truncate px-4 text-muted-foreground">{e.subject}</td>
                  <td className="px-4 whitespace-nowrap tabular-nums">
                    {e.ticketNumber != null ? `#${e.ticketNumber}` : '—'}
                  </td>
                  <td className="px-4 whitespace-nowrap">{statusBadge(e)}</td>
                  <td className="max-w-64 truncate px-4 text-muted-foreground">{e.error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
